import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Add, Remove } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";
import type { ISpeedControlParameterDto } from "@framework/types";

import { ANALYTICS_MEASURE_TYPE } from "../../../scheme/components/analytics/interfaces";
import type { IMultiAnalysisTimeRange, IStructureTreePoint } from "../../types";
import { fetchMultiAnalysisMeasureOptions } from "../../utils/measure-options";
import type { IAnalysisTemplateScalarMeasure } from "../../utils/analysis-template-settings";
import {
  isFrequencyRangeBoundsInvalid,
  MANUAL_TEMPLATE_PRESENTATION_OPTIONS,
  MANUAL_TEMPLATE_UNIT_OPTIONS,
  normalizeFrequencyRangeParamInput,
  normalizeManualTemplateSelectValue,
  parseFrequencyRangeParam,
} from "../../utils/template-filters";
import {
  buildScalarMeasureMetadataFromOptions,
  buildScalarMeasureRowLabel,
  collectParameterUnitOptions,
  createEmptyScalarMeasure,
  groupScalarMeasureRows,
  nextParameterUnit,
  PARAMETER_UNIT_OPTIONS,
  SCALAR_MEASURE_TYPE_OPTIONS,
  scalarMeasureMatchesRow,
  toggleScalarMeasureRow,
  type IParameterUnitOption,
  type IScalarMeasureRow,
} from "../../utils/template-scalar-measures";

const useScalarMeasureRows = ({
  enabled,
  points,
  startTimestamp,
  endTimestamp,
}: {
  enabled: boolean;
  points: readonly IStructureTreePoint[];
  startTimestamp: string;
  endTimestamp: string;
}): { rows: IScalarMeasureRow[]; isLoading: boolean } => {
  const [rows, setRows] = useState<IScalarMeasureRow[]>([]);
  const pointIdsKey = useMemo(
    () =>
      points
        .map(point => point.id)
        .sort((left, right) => left - right)
        .join(","),
    [points],
  );
  const requestIdRef = useRef(0);
  const { fn: loadRows, isLoading } = useApiCall(
    async (api): Promise<IScalarMeasureRow[]> => {
      const pointIds = pointIdsKey
        .split(",")
        .filter(Boolean)
        .map(id => Number(id))
        .filter(Number.isFinite);

      if (!pointIds.length) {
        return [];
      }

      const metadata = (
        await Promise.all(
          points.map(async point => {
            const measureOptions = await fetchMultiAnalysisMeasureOptions(api, ANALYTICS_MEASURE_TYPE.TREND, [
              point.id,
            ]);

            return buildScalarMeasureMetadataFromOptions(measureOptions, [point]);
          }),
        )
      ).flat();

      return groupScalarMeasureRows(metadata);
    },
    { success: false, error: false },
  );
  const loadRowsRef = useRef(loadRows);

  useEffect(() => {
    loadRowsRef.current = loadRows;
  }, [loadRows]);

  useEffect(() => {
    if (!enabled || !pointIdsKey) {
      setRows([]);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    void loadRowsRef
      .current()
      .then(nextRows => {
        if (requestIdRef.current === requestId) {
          setRows(nextRows ?? []);
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          setRows([]);
        }
      });
  }, [enabled, endTimestamp, pointIdsKey, startTimestamp]);

  return { rows, isLoading };
};

const useTrainParameterUnits = (
  trainId: number | null,
  enabled: boolean,
): { unitOptions: IParameterUnitOption[]; isLoading: boolean } => {
  const [unitOptions, setUnitOptions] = useState<IParameterUnitOption[]>([]);
  const requestIdRef = useRef(0);
  const { fn: loadParameters, isLoading } = useApiCall(
    (api, idTrain: number) =>
      api.fetchJson({
        url: `/trains/${idTrain}/speed-control-parameters`,
        method: "GET",
      }) as Promise<ISpeedControlParameterDto[]>,
    { success: false, error: false },
  );
  const loadParametersRef = useRef(loadParameters);

  useEffect(() => {
    loadParametersRef.current = loadParameters;
  }, [loadParameters]);

  useEffect(() => {
    if (!enabled || trainId === null) {
      setUnitOptions([]);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    void loadParametersRef
      .current(undefined, trainId)
      .then(parameters => {
        if (requestIdRef.current === requestId) {
          setUnitOptions(collectParameterUnitOptions(parameters ?? []));
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          setUnitOptions([]);
        }
      });
  }, [enabled, trainId]);

  return { unitOptions, isLoading };
};

interface ITrendsTemplateScalarsEditorProps {
  configureFiltersManually: boolean;
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisTimeRange;
  trainId: number | null;
  scalarMeasures: readonly IAnalysisTemplateScalarMeasure[];
  parameterUnits: readonly string[];
  onScalarMeasuresChange: (measures: IAnalysisTemplateScalarMeasure[]) => void;
  onParameterUnitsChange: (units: string[]) => void;
}

const toNullableNumber = (value: string): number | null => {
  const normalized = normalizeManualTemplateSelectValue(value);

  if (!normalized) {
    return null;
  }

  const numericValue = Number(normalized);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const toNullableFrequency = (value: string): number | null => parseFrequencyRangeParam(value);

const CheckboxSelectionTable: FC<{
  rows: readonly { key: string; label: string; checked: boolean }[];
  nameHeaderId: string;
  emptyLabelId: string;
  isLoading: boolean;
  onToggle: (key: string, checked: boolean) => void;
}> = ({ rows, nameHeaderId, emptyLabelId, isLoading, onToggle }) => (
  <Box sx={{ border: "1px solid #d8ddea", borderRadius: 1, overflow: "hidden" }}>
    <ProgressOverlay isLoading={isLoading} wrapperSx={{ minHeight: 120 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>
              <FormattedMessage id={nameHeaderId} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length ? (
            rows.map(row => (
              <TableRow key={row.key} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={row.checked}
                    onChange={event => onToggle(row.key, event.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 12 }}>{row.label}</Typography>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2}>
                <Typography sx={{ fontSize: 12, color: "#8b93a7", py: 1.5, textAlign: "center" }}>
                  <FormattedMessage id={emptyLabelId} />
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </ProgressOverlay>
  </Box>
);

const ManualScalarMeasureCard: FC<{
  measure: IAnalysisTemplateScalarMeasure;
  onChange: (measure: IAnalysisTemplateScalarMeasure) => void;
  onRemove: () => void;
}> = ({ measure, onChange, onRemove }) => {
  const { formatMessage } = useIntl();
  const param1 = measure.param1 === null ? "" : String(measure.param1);
  const param2 = measure.param2 === null ? "" : String(measure.param2);
  const frequencyRangeError = isFrequencyRangeBoundsInvalid(param1, param2);
  const frequencyRangeErrorMessage = frequencyRangeError
    ? formatMessage({ id: "pages.multiAnalysis.templateEditor.frequencyRangeInvalid" })
    : undefined;

  const updateFrequency = (field: "param1" | "param2", value: string) => {
    onChange({
      ...measure,
      [field]: toNullableFrequency(value),
    });
  };

  return (
    <Stack spacing={1} sx={{ p: 1, border: "1px solid #d8ddea", borderRadius: 1 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          select
          size="small"
          label={<FormattedMessage id="pages.multiAnalysis.trendsPanel.measureType" />}
          value={measure.type === null ? "" : String(measure.type)}
          onChange={event => onChange({ ...measure, type: Number(event.target.value) })}
          sx={{ flex: 1 }}
        >
          {SCALAR_MEASURE_TYPE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {formatMessage({ id: `enums.measurementType.${option}` })}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title={formatMessage({ id: "pages.multiAnalysis.trendsPanel.removeScalarMeasure" })}>
          <IconButton
            size="small"
            aria-label={formatMessage({ id: "pages.multiAnalysis.trendsPanel.removeScalarMeasure" })}
            onClick={onRemove}
          >
            <Remove fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          select
          size="small"
          label={<FormattedMessage id="pages.multiAnalysis.templateEditor.units" />}
          value={measure.units === null ? "" : String(measure.units)}
          onChange={event => onChange({ ...measure, units: toNullableNumber(event.target.value) })}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">
            <FormattedMessage id="pages.multiAnalysis.templateEditor.anyValue" />
          </MenuItem>
          {MANUAL_TEMPLATE_UNIT_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {formatMessage({ id: `enums.units.${option}` })}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={<FormattedMessage id="pages.multiAnalysis.templateEditor.presentation" />}
          value={measure.presentation === null ? "" : String(measure.presentation)}
          onChange={event => onChange({ ...measure, presentation: toNullableNumber(event.target.value) })}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">
            <FormattedMessage id="pages.multiAnalysis.templateEditor.anyValue" />
          </MenuItem>
          {MANUAL_TEMPLATE_PRESENTATION_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {formatMessage({ id: `enums.unitsPresentation.${option}` })}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Box>
        <Typography sx={{ fontSize: 11, color: "#6a7284", mb: 0.5 }}>
          <FormattedMessage id="pages.multiAnalysis.templateEditor.frequencyRange" />
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type="number"
            inputProps={{ step: 0.01 }}
            value={param1}
            error={frequencyRangeError}
            helperText={frequencyRangeError ? frequencyRangeErrorMessage : undefined}
            onChange={event => updateFrequency("param1", event.target.value)}
            onBlur={() => updateFrequency("param1", normalizeFrequencyRangeParamInput(param1))}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="number"
            inputProps={{ step: 0.01 }}
            value={param2}
            error={frequencyRangeError}
            helperText={frequencyRangeError ? frequencyRangeErrorMessage : undefined}
            onChange={event => updateFrequency("param2", event.target.value)}
            onBlur={() => updateFrequency("param2", normalizeFrequencyRangeParamInput(param2))}
            sx={{ flex: 1 }}
          />
          <Typography sx={{ fontSize: 12, color: "#6a7284" }}>
            <FormattedMessage id="pages.multiAnalysis.templateEditor.hertz" />
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
};

export const TrendsTemplateScalarsEditor: FC<ITrendsTemplateScalarsEditorProps> = ({
  configureFiltersManually,
  points,
  range,
  trainId,
  scalarMeasures,
  parameterUnits,
  onScalarMeasuresChange,
  onParameterUnitsChange,
}) => {
  const { formatMessage } = useIntl();
  const { rows, isLoading: isMeasuresLoading } = useScalarMeasureRows({
    enabled: !configureFiltersManually && points.length > 0,
    points,
    startTimestamp: String(range.start ?? ""),
    endTimestamp: String(range.end ?? ""),
  });
  const { unitOptions, isLoading: isParametersLoading } = useTrainParameterUnits(trainId, !configureFiltersManually);
  const measureRows = useMemo(
    () =>
      rows.map(row => ({
        key: row.key,
        label: buildScalarMeasureRowLabel(row),
        checked: scalarMeasures.some(measure => scalarMeasureMatchesRow(measure, row)),
      })),
    [rows, scalarMeasures],
  );
  const parameterRows = useMemo(
    () =>
      unitOptions.map(option => ({
        key: option.value,
        label: option.kind === "keyphasor" ? formatMessage({ id: `enums.units.${option.value}` }) : option.value,
        checked: parameterUnits.includes(option.value),
      })),
    [formatMessage, parameterUnits, unitOptions],
  );
  const rowsByKey = useMemo(() => new Map(rows.map(row => [row.key, row])), [rows]);

  const handleToggleMeasure = (rowKey: string, checked: boolean) => {
    const row = rowsByKey.get(rowKey);

    if (!row) {
      return;
    }

    onScalarMeasuresChange(toggleScalarMeasureRow(scalarMeasures, row, checked));
  };

  const handleToggleParameterUnit = (unitKey: string, checked: boolean) => {
    onParameterUnitsChange(
      checked ? Array.from(new Set([...parameterUnits, unitKey])) : parameterUnits.filter(unit => unit !== unitKey),
    );
  };

  const updateManualMeasure = (index: number, measure: IAnalysisTemplateScalarMeasure) => {
    onScalarMeasuresChange(scalarMeasures.map((item, itemIndex) => (itemIndex === index ? measure : item)));
  };

  const updateManualParameterUnit = (index: number, unitValue: string) => {
    onParameterUnitsChange(parameterUnits.map((unit, unitIndex) => (unitIndex === index ? unitValue : unit)));
  };

  return (
    <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0, overflow: "auto", p: 1 }}>
      <Typography sx={{ fontSize: 12, color: "#6a7284" }}>
        <FormattedMessage id="pages.multiAnalysis.trendsPanel.editorHint" />
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ alignItems: "flex-start" }}>
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#3f6db5" }}>
              <FormattedMessage id="pages.multiAnalysis.trendsPanel.scalarMeasures" />
            </Typography>
            {configureFiltersManually ? (
              <Tooltip title={formatMessage({ id: "pages.multiAnalysis.trendsPanel.addScalarMeasure" })}>
                <IconButton
                  size="small"
                  aria-label={formatMessage({ id: "pages.multiAnalysis.trendsPanel.addScalarMeasure" })}
                  onClick={() => onScalarMeasuresChange([...scalarMeasures, createEmptyScalarMeasure()])}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
          {configureFiltersManually ? (
            scalarMeasures.length ? (
              scalarMeasures.map((measure, index) => (
                <ManualScalarMeasureCard
                  key={`${measure.name ?? "manual"}:${index}`}
                  measure={measure}
                  onChange={nextMeasure => updateManualMeasure(index, nextMeasure)}
                  onRemove={() => onScalarMeasuresChange(scalarMeasures.filter((_, itemIndex) => itemIndex !== index))}
                />
              ))
            ) : (
              <Typography sx={{ fontSize: 12, color: "#8b93a7" }}>
                <FormattedMessage id="pages.multiAnalysis.templateEditor.noMeasures" />
              </Typography>
            )
          ) : (
            <CheckboxSelectionTable
              rows={measureRows}
              nameHeaderId="pages.multiAnalysis.templateEditor.measureName"
              emptyLabelId="pages.multiAnalysis.templateEditor.noMeasures"
              isLoading={isMeasuresLoading}
              onToggle={handleToggleMeasure}
            />
          )}
        </Stack>
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#3f6db5" }}>
              <FormattedMessage id="pages.multiAnalysis.trendsPanel.parameters" />
            </Typography>
            {configureFiltersManually ? (
              <Tooltip title={formatMessage({ id: "pages.multiAnalysis.trendsPanel.addParameterUnits" })}>
                <IconButton
                  size="small"
                  aria-label={formatMessage({ id: "pages.multiAnalysis.trendsPanel.addParameterUnits" })}
                  onClick={() => onParameterUnitsChange([...parameterUnits, nextParameterUnit(parameterUnits)])}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
          {configureFiltersManually ? (
            parameterUnits.length ? (
              parameterUnits.map((unitValue, index) => (
                <Stack key={`${unitValue}:${index}`} direction="row" spacing={1} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label={<FormattedMessage id="pages.multiAnalysis.templateEditor.units" />}
                    value={
                      (PARAMETER_UNIT_OPTIONS as readonly string[]).includes(unitValue)
                        ? unitValue
                        : PARAMETER_UNIT_OPTIONS[0]
                    }
                    onChange={event => updateManualParameterUnit(index, event.target.value)}
                    sx={{ flex: 1 }}
                  >
                    {PARAMETER_UNIT_OPTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {formatMessage({ id: `enums.units.${option}` })}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title={formatMessage({ id: "pages.multiAnalysis.trendsPanel.removeParameterUnits" })}>
                    <IconButton
                      size="small"
                      aria-label={formatMessage({ id: "pages.multiAnalysis.trendsPanel.removeParameterUnits" })}
                      onClick={() =>
                        onParameterUnitsChange(parameterUnits.filter((_, unitIndex) => unitIndex !== index))
                      }
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))
            ) : (
              <Typography sx={{ fontSize: 12, color: "#8b93a7" }}>
                <FormattedMessage id="pages.multiAnalysis.trendsPanel.noParameters" />
              </Typography>
            )
          ) : (
            <CheckboxSelectionTable
              rows={parameterRows}
              nameHeaderId="pages.multiAnalysis.templateEditor.units"
              emptyLabelId="pages.multiAnalysis.trendsPanel.noParameters"
              isLoading={isParametersLoading}
              onToggle={handleToggleParameterUnit}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
